/**
 * Dock Service — left-nav menu configuration and entitlement filtering.
 *
 * Raw configuration is sourced through `createLeftNavConfigAdapter()`.
 * Swapping Phase 1 mock data for the real backend API only requires changing
 * the one line in leftNavConfigAdapter.ts — this file stays untouched.
 *
 * Phase 1 (Sept 2026): MockLeftNavConfigAdapter — static mock payload
 * Phase 2 (Future):   ApiLeftNavConfigAdapter  — GET /api/v1/navigation/left-menu
 */

import { createLeftNavConfigAdapter } from './leftNavConfigAdapter';
import api from './api';
import type { DockFlyoutKind, DockFlyoutConfig } from "kci-design-lib";

export type DockIconMode = 'material-symbol' | 'sprite' | 'image';

export interface DockButtonConfig {
  id: string;
  titleKey: string;
  label?: string;
  kind: DockFlyoutKind;
  icon: string;
  iconMode?: DockIconMode;
  iconClassName?: string;
  iconFallback?: string;
  homePage: string;
  actionType: "navigate" | "flyout";
  flyout?: DockFlyoutConfig;
  showBlueDot?: boolean;
  showRedDot?: boolean;
  /** Order for sorting (10, 20, 30, etc.). Lower numbers appear first. */
  order?: number;
  /** If true, only visible to users with limited deployment entitlement */
  limitedDeploymentOnly?: boolean;
  /** If true, this item can be configured in admin center (Phase 2) */
  isConfigurable?: boolean;
  /** If false, item is disabled in the UI (Phase 2 admin control) */
  isEnabled?: boolean;
  /** If false, item is hidden from UI (Phase 2 admin control) */
  isVisible?: boolean;
}

/**
 * Phase 1 Mock: Determines if user is in limited deployment group.
 * In Phase 2, this will be replaced by backend entitlement service call.
 *
 * Currently: Reads from mock user profile or environment flag
 * Phase 2 will: Call backend profile/entitlement API to determine group membership
 *
 * @returns true if user has limited deployment entitlement, false otherwise
 */
export function isUserInLimitedDeployment(): boolean {
  // Phase 1 Mock: Check for mock flag in sessionStorage for testing
  const mockFlag = sessionStorage.getItem('mock-limited-deployment');
  if (mockFlag !== null) {
    return mockFlag === 'true';
  }

  // Default for Phase 1: return false so limited-deployment items stay gated unless explicitly enabled.
  return false;

}

/**
 * Feature-flag gate for in-progress surfaces (e.g. execution-stream) that
 * aren't ready for general availability. Enable locally via:
 *   localStorage.setItem('maintenance', 'true')
 *
 * @returns true if maintenance mode is enabled in localStorage
 */
export function isMaintenanceModeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('maintenance') === 'true';
}

/**
 * Filters dock buttons based on user entitlements and admin visibility configuration.
 * Applies rules:
 * - Hide buttons where isVisible = false
 * - Hide buttons where isEnabled = false
 * - Hide buttons with limitedDeploymentOnly = true if user not in limited deployment group
 *
 * @param buttons Unfiltered list of dock button configs
 * @returns Filtered list suitable for UI rendering
 */
export function filterMenuByUserEntitlement(buttons: DockButtonConfig[], eligibleOverride?: boolean): DockButtonConfig[] {
  // eligibleOverride = true means the user has workspaces assigned → limited-deployment gate is open
  const isLimitedDeployment = eligibleOverride ?? isUserInLimitedDeployment();
  console.log('[DockService] Filtering buttons:', buttons.length, 'isLimitedDeployment:', isLimitedDeployment);

  const filtered = buttons
    .filter((btn) => {
      // Phase 2: These will come from backend, for now they're in mock config
      if (btn.isVisible === false) {
        console.log(`  ❌ "${btn.label}": isVisible=false`);
        return false;
      }
      if (btn.isEnabled === false) {
        console.log(`  ❌ "${btn.label}": isEnabled=false`);
        return false;
      }
      // Visibility gating: Workspaces only visible to limited deployment users
      if (btn.limitedDeploymentOnly && !isLimitedDeployment) {
        console.log(`  ❌ "${btn.label}": limitedDeploymentOnly=true but user not in limited deployment`);
        return false;
      }
      console.log(`  ✓ "${btn.label}": kept`);
      return true;
    })
    // Sort by order field for deterministic menu ordering
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  console.log('[DockService] After filtering:', filtered.length);
  return filtered;
}

export async function fetchEligibleWorkspaces(): Promise<boolean> {
  const result = await api.fetch<unknown>('/User/workspaces');
  return result === true;
}

export async function fetchDockButtons(): Promise<DockButtonConfig[]> {
  const adapter = createLeftNavConfigAdapter();
  const [rawConfig, eligible] = await Promise.all([
    adapter.fetchRawConfig(),
    fetchEligibleWorkspaces().catch((error) => {
      console.warn('[DockService] fetchEligibleWorkspaces failed, defaulting to hidden:', error);
      return false;
    }),
  ]);
  console.log('[DockService] Workspace eligibility resolved:', eligible);
  return filterMenuByUserEntitlement(rawConfig, eligible);
}